import React, { useEffect, useRef } from 'react';
import './BarcodeScanField.css';

/**
 * Barcode input tuned for USB scanner guns (keyboard wedge) and manual entry.
 * Scanners usually type digits quickly and send Enter — handled via onScan.
 */
export default function BarcodeScanField({
  value,
  onChange,
  onScan,
  loading = false,
  disabled = false,
  autoFocus = false,
  label = 'Scan barcode',
  placeholder = 'Scan or type barcode, then Enter',
  hint = null,
  hintTone = 'muted',
  id = 'barcode-scan-input',
  inputClassName = '',
  buttonLabel = 'Find',
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [autoFocus, disabled]);

  const runScan = () => {
    const code = String(value || '').trim();
    if (!code || loading || disabled || !onScan) return;
    onScan(code);
  };

  return (
    <div className="barcode-scan-field">
      <label className="barcode-scan-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="barcode-scan-field__row">
        <input
          ref={inputRef}
          id={id}
          className={`barcode-scan-field__input${inputClassName ? ` ${inputClassName}` : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              runScan();
            }
          }}
          placeholder={placeholder}
          disabled={disabled || loading}
          aria-busy={loading}
        />
        <button
          type="button"
          className="barcode-scan-field__btn"
          onClick={runScan}
          disabled={disabled || loading || !String(value || '').trim()}
        >
          {loading ? '…' : buttonLabel}
        </button>
      </div>
      {hint ? (
        <p
          className={`barcode-scan-field__hint${
            hintTone === 'ok' ? ' barcode-scan-field__hint--ok' : ''
          }${hintTone === 'warn' ? ' barcode-scan-field__hint--warn' : ''}`}
        >
          {hint}
        </p>
      ) : (
        <p className="barcode-scan-field__hint barcode-scan-field__hint--muted">
          Point your scanner here — most devices fill this field and press Enter automatically.
        </p>
      )}
    </div>
  );
}
